import { ExpressionNode } from "./ast";
import { getExpressionKeyPath } from "./expression";
import { isValidSuggestion, Suggestions } from "./suggestions";
import { toProgram } from "./toProgram";
import { toSource } from "./toSource";

const validateExpressionKeyPath = (current: Suggestions | true, keyPath: string[] = [], lookBack: string[] = []): true => {
  if (keyPath.length === 0) {
    /**
     * We can only terminate the recursion if the current suggestion ends in `true`.
     */
    if (isValidSuggestion(current)) {
      return true;
    }

    /**
     * This is true if the keyPath was empty to begin with.
     */
    if (lookBack.length === 0) {
      throw new Error(`Invalid expression "\${}"`);
    }

    /**
     * If we're out of keys but the current suggestion is not true, then the keyPath
     * was incomplete.
     */
    throw new Error(`"${lookBack.join(".")}" is not a valid variable`);
  }

  const [key, ...rest] = keyPath;
  const nextLookBack = [...lookBack, key];

  /**
   * If the current suggestion is true, but we still have keys, then the keyPath
   * is too long.
   */
  if (current === true) {
    throw new Error(`"${nextLookBack.join(".")}" is not a valid variable`);
  }

  const nextSuggestions = current[key];

  if (nextSuggestions === undefined) {
    throw new Error(`"${nextLookBack.join(".")}" is not a valid variable`);
  }

  return validateExpressionKeyPath(nextSuggestions, rest, nextLookBack);
};

export const validateSource = (source: string, suggestions: Suggestions): string => {
  const program = toProgram(source);

  const expressions = program.body.filter((node): node is ExpressionNode => node.type === "Expression");

  for (const expression of expressions) {
    const keyPath: string[] = getExpressionKeyPath(expression);
    validateExpressionKeyPath(suggestions, keyPath);
  }

  return toSource(program);
};
